import { useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiErrorStatus } from '@/services/inspection-modification.service';
import { REQUEST_THREAD_KEY } from '@/services/request-thread.service';
import { getApiErrorMessage } from '@/lib/api-error';
import { toast } from '@/lib/swal';

/**
 * Every Request Thread action runs through here.
 *
 * ── WHY ONE HOOK ────────────────────────────────────────────────────────────
 * Eight actions, one set of rules about what happens after one: the canonical
 * thread query is invalidated so state, owner, waitingFor, the timeline AND the
 * capabilities all come back from the server rather than being patched locally.
 * A capability is the server's answer to "what may this caller do NOW" — the
 * moment an action lands, that answer may be different, and guessing the new one
 * in the client is exactly how a screen starts disagreeing with the API.
 *
 * ── A CAPABILITY IS NOT A GUARANTEE ─────────────────────────────────────────
 * `canClose === true` means the server said so when the thread was READ. By the
 * time the button is pressed the round may have moved, the permission may have
 * been revoked, or another user may have acted. So every failure is surfaced in
 * words, never swallowed, and a 403/404/409 ALSO re-reads the thread: those three
 * mean "your picture is out of date", and the right response is to replace the
 * picture rather than leave a stale action panel on screen.
 */

/** The message a user can act on, per status. Same vocabulary the existing
 * modification panels use, so a failure reads the same wherever it happens. */
function describeWriteFailure(error: unknown, fallback: string): string {
  switch (apiErrorStatus(error)) {
    case 403:
      return 'You do not have permission to take that action on this request.';
    case 404:
      return 'This request is no longer available.';
    case 409:
      return 'This request has moved on since you opened it. The latest status is shown now.';
    default:
      return getApiErrorMessage(error, fallback);
  }
}

/** 403 / 404 / 409 all mean the same thing to a screen: what you are looking at
 * is no longer true. */
function isStale(error: unknown): boolean {
  const status = apiErrorStatus(error);
  return status === 403 || status === 404 || status === 409;
}

export interface ThreadActionOptions<TVars> {
  roundId: string;
  /** Calls an existing service function. No endpoint is invented here. */
  run: (vars: TVars) => Promise<unknown>;
  successMessage: string | ((vars: TVars) => string);
  failureMessage: string;
  /** Clears the form. Only called on success — a failed submit keeps the user's
   * text, which the forms standard requires. */
  onSuccess?: () => void;
}

export function useThreadAction<TVars>({
  roundId,
  run,
  successMessage,
  failureMessage,
  onSuccess,
}: ThreadActionOptions<TVars>) {
  const qc = useQueryClient();
  // Belt and braces with `isPending`: a double-click can fire twice before React
  // has re-rendered the disabled button, and two of these are two transitions.
  const inFlight = useRef(false);

  const mutation = useMutation({
    mutationFn: (vars: TVars) => run(vars),
    onSuccess: (_result, vars) => {
      toast.success(typeof successMessage === 'function' ? successMessage(vars) : successMessage);
      onSuccess?.();
    },
    onError: (error: Error) => {
      toast.error(describeWriteFailure(error, failureMessage));
    },
    onSettled: (_data, error) => {
      inFlight.current = false;
      // Re-read on success, and on a stale failure. Both are "the server knows
      // something this screen does not".
      if (!error || isStale(error)) {
        void qc.invalidateQueries({ queryKey: [REQUEST_THREAD_KEY, roundId] });
      }
    },
  });

  return {
    busy: mutation.isPending,
    /** Returns false when a submit was swallowed as a duplicate. */
    submit: (vars: TVars): boolean => {
      if (inFlight.current || mutation.isPending) return false;
      inFlight.current = true;
      mutation.mutate(vars);
      return true;
    },
  };
}
